<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { Search, Check } from 'lucide-vue-next'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { Input } from '@/components/ui/input'
  import { Button } from '@/components/ui/button'
  import { getInitials } from '@/lib/utils'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      search: 'Search',
      done: 'Done',
      cancel: 'Cancel',
      noResults: 'No results found'
    },
    de: {
      search: 'Suchen',
      done: 'Fertig',
      cancel: 'Abbrechen',
      noResults: 'Keine Ergebnisse gefunden'
    },
    fr: {
      search: 'Rechercher',
      done: 'Terminé',
      cancel: 'Annuler',
      noResults: 'Aucun résultat trouvé'
    }
  })

  export interface ClerkOption {
    uuid: string
    firstName: string
    lastName: string
    avatar?: string | null
  }

  const props = withDefaults(
    defineProps<{
      clerkOptions: ClerkOption[]
      currentClerkIds: string[]
      maxClerks?: number
      popoverWidth?: string
      align?: 'start' | 'center' | 'end'
    }>(),
    {
      maxClerks: 3,
      popoverWidth: '250px',
      align: 'start'
    }
  )

  const emit = defineEmits<{
    confirm: [clerkIds: string[]]
    cancel: []
  }>()

  const isOpen = ref(false)
  const pendingClerkIds = ref<string[]>([])
  const searchQuery = ref('')

  watch(isOpen, (open) => {
    if (open) {
      pendingClerkIds.value = [...props.currentClerkIds]
      searchQuery.value = ''
    }
  })

  const filteredOptions = computed(() => {
    const query = searchQuery.value.toLowerCase()
    if (!query) return props.clerkOptions
    return props.clerkOptions.filter((clerk) => {
      const fullName = `${clerk.firstName} ${clerk.lastName}`.toLowerCase()
      return fullName.includes(query)
    })
  })

  const isAtLimit = computed(() => pendingClerkIds.value.length >= props.maxClerks)

  const hasChanges = computed(() => {
    const current = [...props.currentClerkIds].sort()
    const pending = [...pendingClerkIds.value].sort()
    if (current.length !== pending.length) return true
    return current.some((id, i) => id !== pending[i])
  })

  const isSelected = (uuid: string) => pendingClerkIds.value.includes(uuid)

  const isDisabled = (uuid: string) => isAtLimit.value && !isSelected(uuid)

  const toggleClerk = (uuid: string) => {
    if (isDisabled(uuid)) return
    const index = pendingClerkIds.value.indexOf(uuid)
    if (index === -1) {
      pendingClerkIds.value.push(uuid)
    } else {
      pendingClerkIds.value.splice(index, 1)
    }
  }

  const getFullName = (clerk: ClerkOption) => `${clerk.firstName} ${clerk.lastName}`

  const handleDone = () => {
    if (hasChanges.value) {
      emit('confirm', [...pendingClerkIds.value])
    }
    isOpen.value = false
  }

  const handleCancel = () => {
    emit('cancel')
    isOpen.value = false
  }

  const handleEscape = (e: Event) => {
    e.preventDefault()
    handleCancel()
  }
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger as-child>
      <slot />
    </PopoverTrigger>
    <PopoverContent
      class="p-0"
      :style="{ width: popoverWidth }"
      :align="align"
      @escape-key-down="handleEscape"
    >
      <!-- Search -->
      <div class="p-2">
        <div class="relative">
          <Search class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            v-model="searchQuery"
            type="text"
            :placeholder="t('search')"
            class="h-8 pl-8 text-sm"
            autocomplete="off"
          />
        </div>
      </div>

      <!-- Clerk list -->
      <div class="max-h-[200px] overflow-y-auto border-t border-gray-100">
        <button
          v-for="clerk in filteredOptions"
          :key="clerk.uuid"
          class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors"
          :class="isDisabled(clerk.uuid) ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-50'"
          :disabled="isDisabled(clerk.uuid)"
          @click="toggleClerk(clerk.uuid)"
        >
          <!-- Checkbox -->
          <div
            class="flex h-5 w-5 shrink-0 items-center justify-center rounded border"
            :class="
              isSelected(clerk.uuid)
                ? 'border-purple-500 bg-purple-500 text-white'
                : 'border-gray-300'
            "
          >
            <Check
              v-if="isSelected(clerk.uuid)"
              class="h-3.5 w-3.5"
            />
          </div>
          <!-- Avatar -->
          <div
            class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-500 text-[10px] font-medium
              text-white"
          >
            {{ getInitials(getFullName(clerk)) }}
          </div>
          <!-- Name -->
          <span class="truncate">{{ getFullName(clerk) }}</span>
        </button>
        <div
          v-if="filteredOptions.length === 0"
          class="px-3 py-4 text-center text-sm text-gray-500"
        >
          {{ t('noResults') }}
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-2 border-t border-gray-100 p-2">
        <Button
          variant="ghost"
          size="sm"
          @click="handleCancel"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          size="sm"
          :disabled="!hasChanges"
          @click="handleDone"
        >
          {{ t('done') }}
        </Button>
      </div>
    </PopoverContent>
  </Popover>
</template>
